"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Plus, Trash2, Edit2, Save, X, RefreshCw, ExternalLink, Github, Layout, Eye, EyeOff } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Project {
  id: string;
  title: string;
  category: string;
  description: string;
  full_description: string;
  image_url: string;
  tags: string[];
  tech_stack: string[];
  challenges: string[];
  link_url: string;
  github_url: string;
  is_visible: boolean;
}

export function ProjectsManager() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<Project>>({});

  const fetchProjects = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      toast.error("Failed to fetch projects");
    } else {
      setProjects(data || []);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const startEditing = (project: Project) => {
    setIsEditing(project.id);
    setEditForm(project);
  };

  const handleSave = async (id: string | 'new') => {
    const isNew = id === 'new';
    const { error } = isNew 
      ? await supabase.from('projects').insert([editForm])
      : await supabase.from('projects').update(editForm).eq('id', id);

    if (error) {
      toast.error(error.message);
    } else {
      toast.success(isNew ? "Project created" : "Project updated");
      setIsEditing(null);
      setEditForm({});
      fetchProjects();
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this project?")) return;
    const { error } = await supabase.from('projects').delete().eq('id', id);
    if (error) toast.error(error.message);
    else fetchProjects();
  };

  if (isLoading) return <div className="p-20 text-center"><RefreshCw className="animate-spin mx-auto text-violet-500" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-black uppercase tracking-tight text-white">Manage Projects</h3>
        <button 
          onClick={() => { setIsEditing('new'); setEditForm({ is_visible: true, tags: [], tech_stack: [], challenges: [] }); }}
          className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-violet-500 transition-all"
        >
          <Plus size={16} /> Add Project
        </button>
      </div>

      <div className="grid gap-6">
        <AnimatePresence>
          {isEditing && (
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="p-8 bg-neutral-900 border border-violet-500/50 rounded-[2rem] space-y-6 shadow-2xl"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-neutral-500">Title</label>
                  <input 
                    className="w-full bg-black border border-neutral-800 rounded-xl px-4 py-3 text-white focus:border-violet-500 outline-none transition-all" 
                    value={editForm.title || ''}
                    onChange={e => setEditForm({...editForm, title: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-neutral-500">Category</label>
                  <input 
                    className="w-full bg-black border border-neutral-800 rounded-xl px-4 py-3 text-white focus:border-violet-500 outline-none transition-all" 
                    value={editForm.category || ''}
                    onChange={e => setEditForm({...editForm, category: e.target.value})}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-neutral-500">Short Description</label>
                <textarea 
                  className="w-full bg-black border border-neutral-800 rounded-xl px-4 py-3 text-white focus:border-violet-500 outline-none transition-all resize-none" 
                  rows={2}
                  value={editForm.description || ''}
                  onChange={e => setEditForm({...editForm, description: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-neutral-500">Full Description</label>
                <textarea 
                  className="w-full bg-black border border-neutral-800 rounded-xl px-4 py-3 text-white focus:border-violet-500 outline-none transition-all resize-none" 
                  rows={4}
                  value={editForm.full_description || ''}
                  onChange={e => setEditForm({...editForm, full_description: e.target.value})}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-neutral-500">Image URL</label>
                  <input 
                    className="w-full bg-black border border-neutral-800 rounded-xl px-4 py-3 text-white focus:border-violet-500 outline-none transition-all" 
                    value={editForm.image_url || ''}
                    onChange={e => setEditForm({...editForm, image_url: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-neutral-500">Live Link</label>
                  <input 
                    className="w-full bg-black border border-neutral-800 rounded-xl px-4 py-3 text-white focus:border-violet-500 outline-none transition-all" 
                    value={editForm.link_url || ''}
                    onChange={e => setEditForm({...editForm, link_url: e.target.value})}
                  />
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button 
                  onClick={() => handleSave(isEditing === 'new' ? 'new' : isEditing)} 
                  className="flex-1 bg-violet-600 hover:bg-violet-500 py-4 rounded-xl font-black uppercase tracking-widest text-xs transition-all shadow-lg shadow-violet-600/20"
                >
                  {isEditing === 'new' ? 'Create Project' : 'Save Changes'}
                </button>
                <button 
                  onClick={() => setIsEditing(null)} 
                  className="px-8 bg-neutral-800 hover:bg-neutral-700 py-4 rounded-xl font-black uppercase tracking-widest text-xs transition-all"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {projects.map(project => (
          <div key={project.id} className="p-6 bg-neutral-900/40 border border-neutral-800 rounded-3xl flex items-center justify-between group hover:border-neutral-700 transition-all">
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 rounded-2xl bg-neutral-800 overflow-hidden border border-neutral-700">
                <img src={project.image_url} alt="" className="w-full h-full object-cover" />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <h4 className="font-black text-white uppercase tracking-tight text-lg">{project.title}</h4>
                  {project.is_visible ? <Eye size={14} className="text-green-500" /> : <EyeOff size={14} className="text-neutral-600" />}
                </div>
                <p className="text-xs text-neutral-500 font-bold uppercase tracking-widest">{project.category}</p>
              </div>
            </div>
            <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={() => startEditing(project)} className="p-3 text-violet-400 hover:bg-violet-500/10 rounded-xl transition-all"><Edit2 size={18} /></button>
              <button onClick={() => handleDelete(project.id)} className="p-3 text-red-500 hover:bg-red-500/10 rounded-xl transition-all"><Trash2 size={18} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}